<script setup lang="ts">
import { cn } from '@/lib/utils'
import { useVModel } from '@vueuse/core'
import type { CountryCode } from 'libphonenumber-js'
import { Eye, EyeClosed } from 'lucide-vue-next'
import { computed, ref, type HTMLAttributes, type InputHTMLAttributes } from 'vue'
import PhoneInput from './PhoneInput.vue'

const props = withDefaults(
    defineProps<{
        defaultValue?: string | number
        modelValue?: string | number | null | File
        class?: HTMLAttributes['class']
        type: InputHTMLAttributes['type']
        placeholder?: string
        defaultCallingCode?: CountryCode
        id: string
    }>(),
    {
        type: 'text',
    },
)

const emits = defineEmits<{
    (e: 'update:modelValue', payload: string | number): void
}>()

const modelValue = useVModel(props, 'modelValue', emits, {
    passive: true,
    defaultValue: props.defaultValue,
})

const phoneModelValue = computed({
    get: () => String(modelValue.value),
    set: (val) => (modelValue.value = val),
})
const fieldType = ref('password')
</script>

<template>
    <div
        v-if="type === 'password'"
        :class="
            cn(
                'border-input placeholder:text-muted-foreground focus-within:ring-primary/50 focus-within:border-primary file:-h-4 relative flex h-12 w-full overflow-hidden rounded-md border bg-transparent text-sm transition-colors file:border-0 file:bg-transparent file:py-3 file:text-sm file:font-medium focus-within:ring-[3px] focus-within:outline-hidden disabled:cursor-not-allowed disabled:opacity-50',
                props.class,
            )
        "
    >
        <input
            :type="fieldType"
            v-model="modelValue"
            :placeholder="placeholder"
            autocomplete="on"
            class="flex-1 appearance-none px-3 py-1 outline-0"
        />
        <div
            role="button"
            @click.prevent="fieldType = fieldType === 'password' ? 'text' : 'password'"
            class="absolute top-1/2 right-2 flex -translate-y-1/2 cursor-pointer items-center justify-center"
        >
            <Eye v-if="fieldType === 'password'" />
            <EyeClosed v-else />
        </div>
    </div>
    <PhoneInput v-else-if="type === 'tel'" v-model="phoneModelValue" :placeholder="placeholder" :default-calling-code="defaultCallingCode" :id="id" />
    <input
        v-else
        v-model="modelValue"
        :placeholder="placeholder"
        :type="type"
        :class="
            cn(
                'border-input placeholder:text-muted-foreground focus-visible:ring-primary/50 focus-visible:border-primary flex h-12 w-full rounded-md border bg-transparent px-3 py-1 text-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:ring-[3px] focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50',
                props.class,
            )
        "
    />
</template>
